import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, User, Calendar, Clock, AlertTriangle,
  CheckCircle2, Loader2, FileText, Send, Download, Users,
  AlertCircle, ChevronRight, Flag, Zap, FileQuestion, Bell
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const PARTY_COLORS = {
  'Contractor': 'bg-blue-100 text-blue-700 border-blue-200',
  'Consultant': 'bg-purple-100 text-purple-700 border-purple-200',
  'Subcontractor': 'bg-amber-100 text-amber-700 border-amber-200',
  'Client': 'bg-green-100 text-green-700 border-green-200',
  'Internal': 'bg-slate-100 text-slate-700 border-slate-200'
};

const PRIORITY_COLORS = {
  'Low': 'bg-slate-100 text-slate-700',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-amber-100 text-amber-700',
  'Urgent': 'bg-red-100 text-red-700'
};

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  'Awaiting Response': 'bg-purple-100 text-purple-700',
  'Blocked': 'bg-red-100 text-red-700',
  'Closed': 'bg-green-100 text-green-700'
};

const ActionPacks = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [actionPacks, setActionPacks] = useState(null);
  const [overdueData, setOverdueData] = useState(null);
  const [selectedParty, setSelectedParty] = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [escalationType, setEscalationType] = useState('RFI');
  const [escalating, setEscalating] = useState(false);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [projectInfo, setProjectInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, [projectId, includeClosed]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load action packs
      const packsResponse = await api.get(`/meeting-minutes/actions/by-party/${projectId}?include_closed=${includeClosed}`);
      setActionPacks(packsResponse.data);
      
      // Load overdue actions
      const overdueResponse = await api.get(`/meeting-minutes/actions/overdue/${projectId}`);
      setOverdueData(overdueResponse.data);
      
      // Load project info
      const projectResponse = await api.get(`/projects/${projectId}`);
      setProjectInfo(projectResponse.data);
      
    } catch (error) {
      toast.error('Failed to load action packs');
    } finally {
      setLoading(false);
    }
  };

  const handleEscalate = async () => {
    if (selectedActions.length === 0) {
      toast.error('Please select actions to escalate');
      return;
    }

    setEscalating(true);
    try {
      // Backend expects action_ids as JSON array body and escalation_type as query param
      const response = await api.post(
        `/meeting-minutes/actions/bulk-escalate?escalation_type=${escalationType}`,
        selectedActions
      );
      
      toast.success(`${response.data.results.success.length} actions escalated to ${escalationType}`);
      setSelectedActions([]);
      setEscalateDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to escalate actions');
    } finally {
      setEscalating(false);
    }
  };

  const handleConvertToRFI = async (actionId) => {
    try {
      await api.post(`/meeting-minutes/actions/${actionId}/convert-to-rfi`);
      toast.success('RFI created from action');
      loadData();
    } catch (error) {
      toast.error('Failed to create RFI');
    }
  };

  const handleConvertToNotice = async (actionId) => {
    try {
      await api.post(`/meeting-minutes/actions/${actionId}/convert-to-notice`);
      toast.success('Notice created from action');
      loadData();
    } catch (error) {
      toast.error('Failed to create Notice');
    }
  };

  const toggleActionSelection = (actionId) => {
    if (selectedActions.includes(actionId)) {
      setSelectedActions(selectedActions.filter(id => id !== actionId));
    } else {
      setSelectedActions([...selectedActions, actionId]);
    }
  };

  const selectAllPartyActions = (party) => {
    const pack = actionPacks?.action_packs?.find(p => p.party === party);
    if (!pack) return;
    
    const partyActionIds = pack.actions.map(a => a.id);
    const allSelected = partyActionIds.every(id => selectedActions.includes(id));
    
    if (allSelected) {
      setSelectedActions(selectedActions.filter(id => !partyActionIds.includes(id)));
    } else {
      const newSelected = [...new Set([...selectedActions, ...partyActionIds])];
      setSelectedActions(newSelected);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="rounded-sm"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-barlow font-bold uppercase tracking-wider text-slate-900">
                Action Packs
              </h1>
              <p className="text-sm text-slate-500">
                {projectInfo?.name || 'Project'} - Actions grouped by responsible party
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 mr-4">
              <Checkbox
                id="include-closed"
                checked={includeClosed}
                onCheckedChange={setIncludeClosed}
              />
              <label htmlFor="include-closed" className="text-sm text-slate-600 cursor-pointer">
                Include closed
              </label>
            </div>
            
            {selectedActions.length > 0 && (
              <Button
                onClick={() => setEscalateDialogOpen(true)}
                className="rounded-sm bg-amber-600 hover:bg-amber-700"
              >
                <Zap className="h-4 w-4 mr-2" />
                Escalate ({selectedActions.length})
              </Button>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Actions</p>
                  <p className="text-2xl font-bold text-slate-900">{actionPacks?.summary?.total_actions || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Parties</p>
                  <p className="text-2xl font-bold text-slate-900">{actionPacks?.summary?.total_parties || 0}</p>
                </div>
                <Users className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-red-600">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">{overdueData?.total_overdue || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-600">Selected</p>
                  <p className="text-2xl font-bold text-amber-600">{selectedActions.length}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Packs by Party */}
        <div className="space-y-4">
          {actionPacks?.action_packs?.map((pack) => (
            <Card key={pack.party} className={`border-2 ${pack.overdue_count > 0 ? 'border-red-200' : 'border-slate-200'}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`rounded-sm text-sm px-3 py-1 ${PARTY_COLORS[pack.party] || 'bg-slate-100'}`}>
                      <Building2 className="h-4 w-4 mr-2" />
                      {pack.party}
                    </Badge>
                    <span className="text-sm text-slate-500">
                      {pack.total_count} action{pack.total_count !== 1 ? 's' : ''}
                    </span>
                    {pack.overdue_count > 0 && (
                      <Badge className="rounded-sm text-xs bg-red-100 text-red-700">
                        {pack.overdue_count} overdue
                      </Badge>
                    )}
                    {pack.high_priority_count > 0 && (
                      <Badge className="rounded-sm text-xs bg-amber-100 text-amber-700">
                        {pack.high_priority_count} high priority
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectAllPartyActions(pack.party)}
                    className="rounded-sm"
                  >
                    {pack.actions.every(a => selectedActions.includes(a.id)) ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pack.actions.map((action) => (
                    <div
                      key={action.id}
                      className={`flex items-start gap-3 p-3 rounded-sm border transition-colors cursor-pointer ${
                        selectedActions.includes(action.id) 
                          ? 'border-blue-400 bg-blue-50' 
                          : action.is_overdue 
                            ? 'border-red-200 bg-red-50/50' 
                            : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => toggleActionSelection(action.id)}
                    >
                      <Checkbox 
                        checked={selectedActions.includes(action.id)} 
                        className="mt-1"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-slate-500">{action.action_number}</span>
                          <Badge className={`text-xs ${STATUS_COLORS[action.status] || ''}`}>
                            {action.status}
                          </Badge>
                          <Badge className={`text-xs ${PRIORITY_COLORS[action.priority] || ''}`}>
                            {action.priority}
                          </Badge>
                          {action.is_overdue && (
                            <Badge className="text-xs bg-red-100 text-red-700">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Overdue
                            </Badge>
                          )}
                          {action.related_rfi_id && (
                            <Badge variant="outline" className="text-xs">RFI Linked</Badge>
                          )}
                          {action.related_notice_id && (
                            <Badge variant="outline" className="text-xs">Notice Linked</Badge>
                          )}
                        </div>
                        <p className="font-medium text-slate-900 mt-1">{action.task_title}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          {action.assigned_to_name && (
                            <span>
                              <User className="h-3 w-3 inline mr-1" />
                              {action.assigned_to_name}
                            </span>
                          )}
                          {action.due_date && (
                            <span className={action.is_overdue ? 'text-red-600 font-medium' : ''}>
                              <Calendar className="h-3 w-3 inline mr-1" />
                              Due: {formatDate(action.due_date)}
                            </span>
                          )}
                          {action.minutes_title && (
                            <span>
                              <FileText className="h-3 w-3 inline mr-1" />
                              {action.minutes_title}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleConvertToRFI(action.id)}
                          className="h-8 w-8 p-0"
                          title="Convert to RFI"
                          disabled={!!action.related_rfi_id}
                        >
                          <FileQuestion className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleConvertToNotice(action.id)}
                          className="h-8 w-8 p-0"
                          title="Convert to Notice"
                          disabled={!!action.related_notice_id}
                        >
                          <Bell className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          
          {(!actionPacks?.action_packs || actionPacks.action_packs.length === 0) && (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No actions found</p>
                <p className="text-sm text-slate-400 mt-1">
                  Upload meeting minutes and extract actions to get started
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Escalate Dialog */}
        <Dialog open={escalateDialogOpen} onOpenChange={setEscalateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                Escalate Actions
              </DialogTitle>
              <DialogDescription>
                Convert {selectedActions.length} selected action(s) to a formal document.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Escalation Type
                </label>
                <Select value={escalationType} onValueChange={setEscalationType}>
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RFI">
                      <div className="flex items-center gap-2">
                        <FileQuestion className="h-4 w-4" />
                        Request for Information (RFI)
                      </div>
                    </SelectItem>
                    <SelectItem value="Notice">
                      <div className="flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        Formal Notice
                      </div>
                    </SelectItem>
                    <SelectItem value="DelayEvent">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Delay Event
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="bg-slate-50 rounded-sm p-3">
                <p className="text-sm text-slate-600">
                  {escalationType === 'RFI' && 'Create RFIs for each action requiring clarification or response from consultants.'}
                  {escalationType === 'Notice' && 'Create formal notices for overdue or critical actions requiring contractual attention.'}
                  {escalationType === 'DelayEvent' && 'Register delay events for actions impacting project timeline.'}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEscalateDialogOpen(false)}
                disabled={escalating}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEscalate}
                disabled={escalating}
                className="rounded-sm bg-amber-600 hover:bg-amber-700"
              >
                {escalating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Escalating...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Escalate {selectedActions.length} Actions
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ActionPacks;
